import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Popover,
  IconButton,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
} from '@mui/icons-material';

// ─── Types ───────────────────────────────────────────────────────────
export interface DateRange {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

export interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  defaultPreset?: string;
}

// ─── Presets ─────────────────────────────────────────────────────────
interface Preset {
  key: string;
  label: string;
}

const PRESETS: Preset[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'this_year', label: 'This Year' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'custom', label: 'Custom Range' },
];

// ─── Date helpers ────────────────────────────────────────────────────
const fmt = (d: Date) => d.toISOString().split('T')[0];

const parseDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isToday = (d: Date) => isSameDay(d, new Date());

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Compute start/end dates from a preset key.
 * Exported so other pages (e.g. DashboardPage) can use it.
 */
export function getDateRange(preset: string): DateRange {
  const today = new Date();
  const todayStr = fmt(today);

  const startOfWeek = (d: Date) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };

  switch (preset) {
    case 'today':
      return { startDate: todayStr, endDate: todayStr };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { startDate: fmt(y), endDate: fmt(y) };
    }
    case 'this_week':
      return { startDate: fmt(startOfWeek(today)), endDate: todayStr };
    case 'last_week': {
      const thisWeekStart = startOfWeek(today);
      const lastWeekEnd = new Date(thisWeekStart);
      lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekStart.getDate() - 6);
      return { startDate: fmt(lastWeekStart), endDate: fmt(lastWeekEnd) };
    }
    case 'this_month':
      return {
        startDate: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
        endDate: todayStr,
      };
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }
    case 'this_quarter': {
      const q = Math.floor(today.getMonth() / 3);
      return {
        startDate: fmt(new Date(today.getFullYear(), q * 3, 1)),
        endDate: todayStr,
      };
    }
    case 'last_quarter': {
      const q = Math.floor(today.getMonth() / 3);
      const start = new Date(today.getFullYear(), (q - 1) * 3, 1);
      const end = new Date(today.getFullYear(), q * 3, 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }
    case 'this_year':
      return {
        startDate: fmt(new Date(today.getFullYear(), 0, 1)),
        endDate: todayStr,
      };
    case 'last_year':
      return {
        startDate: fmt(new Date(today.getFullYear() - 1, 0, 1)),
        endDate: fmt(new Date(today.getFullYear() - 1, 11, 31)),
      };
    default:
      return {
        startDate: fmt(startOfWeek(today)),
        endDate: todayStr,
      };
  }
}

// ─── Calendar grid builder ───────────────────────────────────────────
interface CalendarDay {
  date: Date;
  inMonth: boolean;
}

function buildCalendarGrid(year: number, month: number): CalendarDay[] {
  const firstDay = new Date(year, month, 1);
  // Monday = 0 … Sunday = 6
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const days: CalendarDay[] = [];

  // Previous month overflow
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, inMonth: false });
  }

  // Current month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), inMonth: true });
  }

  // Next month overflow to fill 6 rows (42 cells)
  while (days.length < 42) {
    const next = days.length - startDow - daysInMonth + 1;
    days.push({ date: new Date(year, month + 1, next), inMonth: false });
  }

  return days;
}

// ─── Single calendar component ───────────────────────────────────────
interface MonthCalendarProps {
  year: number;
  month: number;
  onPrev: () => void;
  onNext: () => void;
  onYearChange: (y: number) => void;
  onMonthChange: (m: number) => void;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onDayClick: (d: Date) => void;
  isCustom: boolean;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  year,
  month,
  onPrev,
  onNext,
  onYearChange,
  onMonthChange,
  rangeStart,
  rangeEnd,
  onDayClick,
  isCustom,
}) => {
  const grid = useMemo(() => buildCalendarGrid(year, month), [year, month]);

  const inRange = (d: Date) => {
    if (!rangeStart || !rangeEnd) return false;
    const t = d.getTime();
    return t >= rangeStart.getTime() && t <= rangeEnd.getTime();
  };

  const isStart = (d: Date) => rangeStart && isSameDay(d, rangeStart);
  const isEnd = (d: Date) => rangeEnd && isSameDay(d, rangeEnd);

  const yearOptions = Array.from({ length: 21 }, (_, i) => year - 10 + i);

  return (
    <Box sx={{ width: 280, userSelect: 'none' }}>
      {/* Header: arrows + month/year selects */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton size="small" onClick={onPrev}>
          <ChevronLeft fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Select
            size="small"
            value={month}
            onChange={(e) => onMonthChange(Number(e.target.value))}
            variant="standard"
            disableUnderline
            sx={{ fontSize: 14, fontWeight: 600 }}
          >
            {MONTH_NAMES.map((name, i) => (
              <MenuItem key={i} value={i}>{name}</MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
            variant="standard"
            disableUnderline
            sx={{ fontSize: 14, fontWeight: 600 }}
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </Box>

        <IconButton size="small" onClick={onNext}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>

      {/* Day-of-week labels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', mb: 0.5 }}>
        {DAY_LABELS.map((l) => (
          <Typography key={l} variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', py: 0.5 }}>
            {l}
          </Typography>
        ))}
      </Box>

      {/* Day grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {grid.map((cell, idx) => {
          const dayIsToday = isToday(cell.date);
          const dayIsStart = isStart(cell.date);
          const dayIsEnd = isEnd(cell.date);
          const dayInRange = inRange(cell.date);

          return (
            <Box
              key={idx}
              onClick={() => {
                if (isCustom && cell.inMonth) onDayClick(cell.date);
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 34,
                cursor: isCustom && cell.inMonth ? 'pointer' : 'default',
                // range background band
                ...(dayInRange && !dayIsStart && !dayIsEnd
                  ? { backgroundColor: 'rgba(76,175,80,0.12)' }
                  : {}),
                ...(dayIsStart && dayInRange
                  ? {
                      background: 'linear-gradient(to right, transparent 50%, rgba(76,175,80,0.12) 50%)',
                    }
                  : {}),
                ...(dayIsEnd && dayInRange && !dayIsStart
                  ? {
                      background: 'linear-gradient(to left, transparent 50%, rgba(76,175,80,0.12) 50%)',
                    }
                  : {}),
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  fontSize: 13,
                  fontWeight: dayIsStart || dayIsEnd ? 700 : 400,
                  color: !cell.inMonth
                    ? 'text.disabled'
                    : dayIsStart || dayIsEnd
                    ? '#fff'
                    : dayIsToday
                    ? '#fff'
                    : 'text.primary',
                  backgroundColor:
                    dayIsStart || dayIsEnd
                      ? '#4caf50'
                      : dayIsToday
                      ? '#66bb6a'
                      : 'transparent',
                  '&:hover': isCustom && cell.inMonth
                    ? { backgroundColor: dayIsStart || dayIsEnd ? '#388e3c' : 'action.hover' }
                    : {},
                  transition: 'background-color 0.15s',
                }}
              >
                {cell.date.getDate()}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── Main DateRangeFilter component ──────────────────────────────────
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  defaultPreset = 'this_month',
}) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const [activePreset, setActivePreset] = useState(defaultPreset);
  const [draft, setDraft] = useState<DateRange>(value);
  const [customClicks, setCustomClicks] = useState(0);

  // Left calendar view (year/month)
  const [leftYear, setLeftYear] = useState(() => parseDate(value.startDate).getFullYear());
  const [leftMonth, setLeftMonth] = useState(() => parseDate(value.startDate).getMonth());

  // Right calendar is always one month ahead of left
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = (leftMonth + 1) % 12;

  // Sync draft when external value changes
  useEffect(() => {
    setDraft(value);
  }, [value.startDate, value.endDate]);

  // Parsed range dates for calendar highlighting
  const rangeStart = useMemo(() => (draft.startDate ? parseDate(draft.startDate) : null), [draft.startDate]);
  const rangeEnd = useMemo(() => (draft.endDate ? parseDate(draft.endDate) : null), [draft.endDate]);

  const presetLabel = useMemo(() => {
    const found = PRESETS.find((p) => p.key === activePreset);
    return found ? found.label : 'Select Range';
  }, [activePreset]);

  // ── Open / Close ──
  const handleOpen = () => {
    setDraft(value);
    setCustomClicks(0);
    // Position calendars to show start-date month
    const sd = parseDate(value.startDate);
    setLeftYear(sd.getFullYear());
    setLeftMonth(sd.getMonth());
    setOpen(true);
  };

  const handleCancel = () => setOpen(false);

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  // ── Preset click ──
  const handlePresetClick = (key: string) => {
    setActivePreset(key);
    setCustomClicks(0);
    if (key !== 'custom') {
      const range = getDateRange(key);
      setDraft(range);
      const sd = parseDate(range.startDate);
      setLeftYear(sd.getFullYear());
      setLeftMonth(sd.getMonth());
    }
  };

  // ── Custom day click ──
  const handleDayClick = (d: Date) => {
    if (activePreset !== 'custom') return;
    const dateStr = fmt(d);

    if (customClicks === 0) {
      // First click: set start
      setDraft({ startDate: dateStr, endDate: dateStr });
      setCustomClicks(1);
    } else {
      // Second click: set end (ensure order)
      const start = parseDate(draft.startDate);
      if (d >= start) {
        setDraft({ startDate: draft.startDate, endDate: dateStr });
      } else {
        setDraft({ startDate: dateStr, endDate: draft.startDate });
      }
      setCustomClicks(0);
    }
  };

  // ── Navigation helpers ──
  const navigateLeft = (delta: number) => {
    let m = leftMonth + delta;
    let y = leftYear;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setLeftMonth(m);
    setLeftYear(y);
  };

  return (
    <>
      {/* Trigger button */}
      <Button
        ref={anchorRef}
        variant="outlined"
        size="small"
        startIcon={<CalendarMonth />}
        onClick={handleOpen}
        sx={{
          textTransform: 'none',
          minWidth: 160,
          borderColor: 'divider',
          color: 'text.primary',
          fontWeight: 500,
        }}
      >
        {presetLabel}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { mt: 1, borderRadius: 2, overflow: 'hidden' } },
        }}
      >
        <Box sx={{ display: 'flex', minWidth: 750 }}>
          {/* ── Sidebar with presets ── */}
          <Box
            sx={{
              width: 170,
              borderRight: '1px solid',
              borderColor: 'divider',
              py: 1,
              flexShrink: 0,
            }}
          >
            {PRESETS.map((p) => (
              <Box
                key={p.key}
                onClick={() => handlePresetClick(p.key)}
                sx={{
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  fontWeight: activePreset === p.key ? 700 : 400,
                  fontSize: 14,
                  backgroundColor: activePreset === p.key ? 'action.selected' : 'transparent',
                  '&:hover': { backgroundColor: 'action.hover' },
                  borderRadius: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: activePreset === p.key ? 700 : 400 }}
                >
                  {p.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── Calendars + footer ── */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Two calendars side by side */}
            <Box sx={{ display: 'flex', gap: 2, p: 2, justifyContent: 'center' }}>
              <MonthCalendar
                year={leftYear}
                month={leftMonth}
                onPrev={() => navigateLeft(-1)}
                onNext={() => navigateLeft(1)}
                onYearChange={(y) => setLeftYear(y)}
                onMonthChange={(m) => setLeftMonth(m)}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onDayClick={handleDayClick}
                isCustom={activePreset === 'custom'}
              />
              <MonthCalendar
                year={rightYear}
                month={rightMonth}
                onPrev={() => navigateLeft(0)} // shift left so right moves back
                onNext={() => navigateLeft(2)} // shift left forward so right moves forward
                onYearChange={(y) => {
                  // Adjust left so right lands on chosen year
                  const newLeftYear = rightMonth === 0 ? y - 1 : y;
                  setLeftYear(newLeftYear);
                }}
                onMonthChange={(m) => {
                  // Adjust left so right becomes m
                  const newLeft = m === 0 ? 11 : m - 1;
                  const newLeftYear = m === 0 ? rightYear - 1 : rightYear;
                  setLeftMonth(newLeft);
                  setLeftYear(newLeftYear);
                }}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onDayClick={handleDayClick}
                isCustom={activePreset === 'custom'}
              />
            </Box>

            {/* Footer: date inputs + actions */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  size="small"
                  label="Start date"
                  value={draft.startDate}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, startDate: e.target.value }));
                    setActivePreset('custom');
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 140 }}
                  inputProps={{ style: { fontSize: 13 } }}
                />
                <Typography variant="body2" color="text.secondary">
                  &ndash;
                </Typography>
                <TextField
                  size="small"
                  label="End date"
                  value={draft.endDate}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, endDate: e.target.value }));
                    setActivePreset('custom');
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 140 }}
                  inputProps={{ style: { fontSize: 13 } }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={handleCancel} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApply}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#4caf50',
                    '&:hover': { backgroundColor: '#388e3c' },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeFilter;
